import { useRollbar } from '@rollbar/react'
import { SyntheticEvent, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { loginWithPhoneNumber } from '../../modules/apiclient'
import { BOOK_BASE } from '../../utils/constants'
import { e164ToPhone } from '../../utils/phone'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { TextField } from '../common/fields/TextField'
import { useLoginContext } from './LoginProvider'

export const Login = () => {
    const navigate = usePSNavigate()
    const rollbar = useRollbar()

    const { loginState, setLoginState } = useLoginContext()
    const [searchParams] = useSearchParams()
    const redirectUrl = searchParams.get('redirectUrl')

    const [phoneNumber, setPhoneNumber] = useState(
        loginState.e164 ? e164ToPhone(loginState.e164) : ''
    )

    const sendPhoneNumber = async (e: SyntheticEvent) => {
        e.preventDefault()
        if (loginState.isSending || !phoneNumber) return
        // login with phone number

        setLoginState((s) => ({ ...s, isSending: true }))

        try {
            const resp = await loginWithPhoneNumber(phoneNumber, redirectUrl)
            setLoginState((s) => ({ ...s, ...resp }))
            navigate(redirectUrl ? `/login/code?redirectUrl=${redirectUrl}` : '/login/code')
        }
        catch (error: any) {
            const data = error as { title: string; message: string } | undefined
            if (data?.title && data?.message) {
                alert(`${data.title}\n\n${data.message}`)
            }
            else if (data?.message) {
                alert(`${data.message}`)
            }
            else {
                rollbar.error('A login error has occurred', {
                    error: error.toString ? error.toString() : error,
                })
                console.error('A login error has occurred', error)
                alert('An error has occurred')
            }
        }
        finally {
            setLoginState((s) => ({ ...s, isSending: false }))
        }
    }

    return (
        <div>
            <form onSubmit={sendPhoneNumber}>
                <span className="flex flex-col gap-y-3">
                    <Label>Mobile number</Label>
                    <TextField
                        name="phoneNumber"
                        size="medium"
                        placeholder="Enter mobile number"
                        isFullWidth
                        autoFocus
                        autoComplete="tel"
                        onChange={() => {
                            // Intentionally left blank. This is handled by the onAccept event in the mask library.
                        }}
                        mask={[
                            { mask: '000-000-0000' },
                            {
                                onAccept: (e) => {
                                    setPhoneNumber(e)
                                },
                            },
                        ]}
                        value={phoneNumber}
                    />
                </span>
                <Button
                    intent="primary"
                    type="submit"
                    className="my-6 w-full"
                    disabled={!phoneNumber}
                    isLoading={loginState.isSending}
                >
                    Sign in
                </Button>

                <span className="mx-auto flex items-center justify-center gap-x-1">
                    <Paragraph className="text-gray-600">Don't have an account?</Paragraph>
                    <a
                        href={`${BOOK_BASE}/register?utm_campaign=webv1redir&utm_medium=web&utm_source=desktop`}
                        className="text-paragraph-md text-primary-700 hover:text-primary-700 font-bold underline hover:opacity-60 active:opacity-60"
                    >
                        Sign up
                    </a>
                </span>
            </form>
        </div>
    )
}
